// ============================================================================
// CopilotPage — main route for /copilot. Mobile-first chat surface where
// AMs speak / type and Claude proposes editable action cards. Each card
// commits independently against the existing RLS-gated insert path.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Menu, Plus, Sparkles, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useCopilot } from './hooks/use-copilot';
import { useCopilotConversations } from './hooks/use-copilot-conversations';
import { ChatInput } from './chat-input';
import { ChatMessage } from './chat-message';
import ConversationList from './conversation-list';
import type {
  CopilotProposalBase,
  CopilotToolName,
} from '../../shared/types';

interface FacilityRow {
  facility_id: string;
  facility_name: string;
  account_id: string | null;
}

// CW-I Phase 4 (Option 2, ruled 2026-08-07). The old copy — and these three
// examples — described a FEEDBACK INTAKE BOX, which is exactly why people used
// it as one. The copilot actually carries ~28 intents; every previous example
// was a report-an-incident sentence, so nobody discovered the other 27.
//
// Concrete examples teach capability far better than a category list: people
// copy the SHAPE of the example. One lookup, one log, one how-do-I — three
// different shapes, so the ceiling reads as "anything", not "incidents".
//
// Kept as a single exported array so the set can be rotated later without
// hunting through JSX.
export const EXAMPLE_PROMPTS = [
  'who cleans Rose?',
  'out of towels at Menorah',
  'how do I file a sub change?',
];

export default function CopilotPage() {
  const {
    messages,
    conversationId,
    isSending,
    error,
    failedText,
    streamingText,
    sendMessage,
    loadConversation,
    hasResumableConversation,
    resumeLast,
    commitProposal,
    discardProposal,
    startNewChat,
  } = useCopilot();

  const {
    conversations,
    isLoading: convsLoading,
    showArchived,
    setShowArchived,
    refresh: refreshConversations,
    archive: archiveConversation,
    unarchive: unarchiveConversation,
    rename: renameConversation,
  } = useCopilotConversations();

  // item #650 — the Ask AI bar on Home opens this page with the sentence the
  // person typed. It is put in the box for them to send; nothing is sent for them.
  const location = useLocation();
  const carried = (location.state as { prompt?: unknown } | null)?.prompt;
  const initialPrompt = typeof carried === 'string' ? carried : undefined;

  // Mobile sheet state — the rail is permanently visible on md+ via Tailwind.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  const [committingId, setCommittingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    text: string;
    actionId?: string;
    type: 'success' | 'error' | 'info';
    href?: string;
  } | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  // Words to put in the box: a failed turn comes back, an example fills it.
  const [draft, setDraft] = useState<{ text: string; n: number } | null>(null);
  useEffect(() => {
    if (failedText) setDraft({ text: failedText, n: Date.now() });
  }, [failedText]);
  const handleRetry = () => {
    if (!failedText) return;
    setDraft({ text: '', n: Date.now() });
    void handleSend(failedText);
  };

  // Active facilities — used for the dropdown in cards and the facility →
  // account_id lookup that commits need. Cached for the session.
  const [facilities, setFacilities] = useState<FacilityRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: qErr } = await supabase
        .from('active_facilities')
        .select('facility_id, facility_name, account_id')
        .order('facility_name');
      if (cancelled) return;
      if (qErr) {
        console.warn('[copilot] facilities load failed', qErr);
        return;
      }
      setFacilities((data ?? []) as FacilityRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const facilityOptions = useMemo(
    () =>
      facilities.map((f: FacilityRow) => ({
        facility_id: f.facility_id,
        facility_name: f.facility_name,
      })),
    [facilities],
  );

  const facilityAccountMap = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const f of facilities) {
      m.set(f.facility_id, f.account_id);
    }
    return m;
  }, [facilities]);

  useEffect(() => {
    // 'nearest' scrolls only the chat pane, so the header stays on screen.
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isSending, streamingText]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 10000);
    return () => clearTimeout(t);
  }, [toast]);

  // Build a "View →" link target for a freshly-committed proposal. We route
  // each tool to the most useful detail surface in the app:
  //   feedback     → facility detail (no per-feedback deep link today)
  //   task         → /tasks/{task_id}
  //   supply_order → /supply (the list view)
  function viewLinkFor(
    tool_name: CopilotToolName,
    target_row_id: string | undefined,
    facility_id: string | undefined,
    stopService = false,
  ): string | undefined {
    if (tool_name === 'propose_board_action') return '/build-room';
    if (tool_name === 'propose_task' && target_row_id) {
      return `/tasks/${target_row_id}`;
    }
    if (tool_name === 'propose_supply_order') {
      return '/supply';
    }
    if (tool_name === 'propose_stock_movement') {
      return '/supply/stock';
    }
    if (tool_name === 'propose_close_task' && facility_id) {
      return `/account-management/facilities/${facility_id}`;
    }
    // Every building action: the building, or its Stop Service page.
    if (tool_name === 'propose_building_action' && facility_id) {
      return `/account-management/facilities/${facility_id}${stopService ? '/stop-service' : ''}`;
    }
    return undefined;
  }

  // Are there any proposals still pending review (Proposed state)?
  const hasOpenProposals = useMemo(() => {
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const p of m.proposals ?? []) {
        if (p.status === 'Proposed') return true;
      }
    }
    return false;
  }, [messages]);

  const handleNewChat = () => {
    if (
      hasOpenProposals &&
      !window.confirm(
        'You have proposals that haven’t been committed or discarded. Start a new chat anyway?',
      )
    ) {
      return;
    }
    startNewChat();
    setToast(null);
    setMobileSheetOpen(false);
  };

  // Wrapper around sendMessage that re-queries the list after every turn so
  // the rail reflects the latest title / updated_at — especially important
  // for the very first turn (which is when the server mints the row).
  const handleSend = async (text: string) => {
    await sendMessage(text);
    void refreshConversations();
  };

  // Explicit resume — replaces the mount-time auto-resume deleted in CW-G
  // Phase 1. Same open-proposal guard as New chat, for symmetry.
  const handleResumeLast = async () => {
    await resumeLast();
    setMobileSheetOpen(false);
    setToast(null);
  };

  const handlePickConversation = async (id: string) => {
    if (id === conversationId) {
      setMobileSheetOpen(false);
      return;
    }
    await loadConversation(id);
    setMobileSheetOpen(false);
    setToast(null);
  };

  const handleCommit = async (
    proposal: CopilotProposalBase,
    edited_input: Record<string, unknown>,
    was_edited: boolean,
  ) => {
    setCommittingId(proposal.action_id);
    const res = await commitProposal(
      proposal.action_id,
      edited_input,
      was_edited,
      (fid) => facilityAccountMap.get(fid) ?? null,
    );
    setCommittingId(null);
    if (res.ok) {
      const href = viewLinkFor(
        proposal.tool_name,
        res.target_row_id,
        edited_input.facility_id as string | undefined,
        typeof edited_input.action === 'string' && /service|termination/.test(edited_input.action),
      );
      let text: string;
      if (res.note) {
        // What really happened, when it differs from what was asked.
        text = res.note;
      } else if (proposal.tool_name === 'propose_close_task') {
        const ids = (edited_input.task_ids as string[] | undefined) ?? [];
        text =
          ids.length === 1 ? `Marked 1 task Done` : `Marked ${ids.length} tasks Done`;
      } else if (proposal.tool_name === 'propose_board_action') {
        text = res.target_row_id ?? 'Written to the board';
      } else {
        text = `Saved as ${res.target_row_id ?? 'OK'}`;
      }
      setToast({
        text,
        type: 'success',
        href,
      });
    } else {
      setToast({ text: `Save failed: ${res.error ?? ''}`, type: 'error' });
    }
  };

  const handleDiscard = async (proposal: CopilotProposalBase) => {
    await discardProposal(proposal.action_id);
    setToast({
      text: 'Discarded',
      actionId: proposal.action_id,
      type: 'info',
    });
  };

  const showEmpty = messages.length === 0;

  return (
    // h-full: the shell's <main> already sits between the header and the phone's
    // tab bar, so the composer is on screen without scrolling (was 100dvh-4rem,
    // which forgot the tab bar and hid the box under it).
    <div className="flex h-full min-h-0 bg-tcs-bg-muted">
      {/* Left rail — visible on md+ */}
      <div className="hidden w-60 shrink-0 md:block">
        <ConversationList
          conversations={conversations}
          activeId={conversationId}
          isLoading={convsLoading}
          canResumeLast={hasResumableConversation && messages.length === 0}
          showArchived={showArchived}
          onSelect={handlePickConversation}
          onNew={handleNewChat}
          onResumeLast={handleResumeLast}
          onToggleArchived={setShowArchived}
          onArchive={archiveConversation}
          onUnarchive={unarchiveConversation}
          onRename={renameConversation}
        />
      </div>

      {/* Mobile drawer */}
      {mobileSheetOpen && (
        <div className="fixed inset-0 z-30 flex md:hidden">
          <div className="w-72 max-w-[80vw] bg-tcs-raised shadow-xl">
            <div className="flex items-center justify-between border-b border-tcs-border px-3 py-2">
              <span className="text-sm font-medium text-tcs-text-secondary">Chats</span>
              <button
                type="button"
                onClick={() => setMobileSheetOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded text-tcs-text-muted hover:bg-tcs-surface"
                aria-label="Close chats"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="h-[calc(100%-3.25rem)]">
              <ConversationList
                conversations={conversations}
                activeId={conversationId}
                isLoading={convsLoading}
                canResumeLast={hasResumableConversation && messages.length === 0}
                showArchived={showArchived}
                onSelect={handlePickConversation}
                onNew={handleNewChat}
                onResumeLast={handleResumeLast}
                onToggleArchived={setShowArchived}
                onArchive={archiveConversation}
                onUnarchive={unarchiveConversation}
                onRename={renameConversation}
              />
            </div>
          </div>
          {/* Scrim */}
          <button
            type="button"
            aria-label="Close chats"
            onClick={() => setMobileSheetOpen(false)}
            className="flex-1 bg-tcs-scrim/60"
          />
        </div>
      )}

      {/* Chat pane */}
      <div className="flex flex-1 flex-col">
        {/* Header — always show on mobile (for the hamburger); on desktop
            keep the existing behavior of hiding when empty. */}
        {(!showEmpty || true) && (
          <div className="border-b border-tcs-border bg-tcs-raised px-3 py-2">
            <div className="mx-auto flex max-w-2xl items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-tcs-text-secondary">
                <button
                  type="button"
                  onClick={() => setMobileSheetOpen(true)}
                  className="-my-1 flex h-11 w-11 items-center justify-center rounded text-tcs-text-muted hover:bg-tcs-surface md:hidden"
                  aria-label="Open chats"
                >
                  <Menu className="h-4 w-4" />
                </button>
                <Sparkles className="h-4 w-4" />
                <span>Ask AI</span>
              </div>
              <button
                type="button"
                onClick={handleNewChat}
                className="-my-1 flex min-h-[44px] items-center gap-1 rounded-full border border-tcs-border bg-tcs-raised px-4 text-sm font-medium text-tcs-text-secondary hover:border-tcs-border-input hover:text-tcs-text"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>New chat</span>
              </button>
            </div>
          </div>
        )}

        {/* Scrollable message area */}
        <div className="flex-1 overflow-y-auto px-3 py-4">
          {showEmpty ? (
            <EmptyState onPick={(text) => setDraft({ text, n: Date.now() })} />
          ) : (
            <div
              className="mx-auto flex max-w-2xl flex-col gap-4"
              role="log"
              aria-live="polite"
              aria-relevant="additions"
              aria-label="Conversation with Ask AI"
            >
              {messages.map((m, i) => (
                <ChatMessage
                  key={i}
                  message={m}
                  facilities={facilityOptions}
                  committingId={committingId}
                  onCommit={handleCommit}
                  onDiscard={handleDiscard}
                />
              ))}
              {/* The reply while it is still being written. Hidden from screen
                  readers: the log announces the finished reply once, as an
                  addition, exactly as before streaming (batch 1). */}
              {streamingText && (
                // inert as well, so a link that has only half-arrived cannot be
                // tabbed to; the finished reply's links are the ones to use.
                <div aria-hidden="true" data-testid="ask-ai-streaming" {...({ inert: '' } as Record<string, string>)}>
                  <ChatMessage
                    message={{ role: 'assistant', content: streamingText }}
                    facilities={facilityOptions}
                    committingId={committingId}
                    onCommit={handleCommit}
                    onDiscard={handleDiscard}
                  />
                </div>
              )}
              {isSending && (
                <div role="status" className="flex items-center gap-2 text-sm text-tcs-text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking…</span>
                </div>
              )}
              {error && (
                <div
                  role="alert"
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong"
                >
                  <span>{error}</span>
                  {failedText && (
                    <button
                      type="button"
                      onClick={handleRetry}
                      disabled={isSending}
                      className="min-h-[44px] rounded-md bg-tcs-inverse px-4 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`mx-auto mb-2 flex max-w-2xl items-center justify-between gap-3 rounded-md px-3 py-2 text-sm shadow ${
              toast.type === 'success'
                ? 'bg-st-ok-solid text-st-solid-ink'
                : toast.type === 'error'
                ? 'bg-st-danger-solid text-st-solid-ink'
                : 'bg-tcs-inverse-hover text-tcs-inverse-ink'
            }`}
          >
            <span>{toast.text}</span>
            {toast.type === 'success' && toast.href && (
              <a
                href={toast.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[44px] items-center rounded-md bg-st-solid-ink/20 px-3 text-sm font-medium hover:bg-st-solid-ink/30"
              >
                View →
              </a>
            )}
          </div>
        )}

        <ChatInput sending={isSending} onSend={handleSend} initialText={initialPrompt} draft={draft} />
      </div>
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (text: string) => void }) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 py-10">
      <div className="flex items-center gap-2 text-tcs-text-secondary">
        <Sparkles className="h-5 w-5" />
        <h1 className="text-xl font-semibold">Ask AI</h1>
      </div>
      <p className="max-w-md text-center text-sm text-tcs-text-muted">
        Ask, log, or look something up — by voice or text.
      </p>
      {/* Tappable: each chip populates the input rather than sending, so the
          example is a starting point you can edit, not a committed message.
          min-h-[44px] holds the touch floor. */}
      <div className="mt-2 grid w-full gap-2">
        {EXAMPLE_PROMPTS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            className="min-h-[44px] rounded-lg border border-tcs-border bg-tcs-raised px-3 py-3 text-left text-sm text-tcs-text-secondary hover:border-tcs-border-input"
          >
            &ldquo;{p}&rdquo;
          </button>
        ))}
      </div>
    </div>
  );
}
